'use client';

import { useState } from 'react';
import { GlueErrorResponse } from '@/src/lib/glue/types';
import { Salutation } from '@/src/lib/customer/types';
import { StoreCountryAttributes } from '@/src/lib/store/types';
import * as Yup from 'yup';
import {
  getErrorCodeFromAnyErrorLike,
  getErrorMessageFromAnyErrorLike,
  isAuthenticationErrorMessage,
  isCartContainsNonBuyableItemsErrorMessage,
  isCartItemsPricesCouldNotBeValidatedTryAgainErrorCode,
  isCartItemsPricesUpdatedTryAgainErrorCode,
  isCartNeedsReviewErrorMessage,
  isStripePaymentErrorCode,
  transformApiErrorsForFormik,
  transformExceptionToErrorObject,
} from '@/src/lib/utils/error';
import { Dictionary } from '@/src/lib/dictionary/types';
import { useNotify } from '@/src/lib/notification/notify';
import { NotificationType } from '@/src/app/components/Notification/types';
import { SelectOption } from '@/src/types/select-option';
import { RevalidateTags } from '@/src/lib/http/types';

export function useFormHelpers(dict: Dictionary, countries: Array<StoreCountryAttributes> = []) {
  const [submitLoading, setSubmitLoading] = useState(false);
  const { toastNotification, flashNotification } = useNotify();

  function mapCountryOptions(storeCountries: Array<StoreCountryAttributes>): Array<SelectOption> {
    return storeCountries.map((country) => ({ value: country.iso2Code, label: country.name }));
  }

  const countryOptions: Array<SelectOption> = mapCountryOptions(countries);
  const requiredMessage = dict.messages.form.validation.required;

  const salutationOptions: Array<SelectOption> = [
    {
      value: Salutation.MR,
      label: dict.userProfile.salutation.mr,
    },
    {
      value: Salutation.MS,
      label: dict.userProfile.salutation.ms,
    },
    {
      value: Salutation.NONE,
      label: dict.userProfile.salutation.none,
    },
  ];

  function handleErrors(
    err: string | GlueErrorResponse | Error | unknown,
    useToast: boolean = false,
    setErrors?: (errors: Record<string, string>) => void,
  ): {
    triggerPageRefreshAndLogout: boolean;
    triggerPageRefresh: boolean;
    revalidateTag: RevalidateTags | undefined;
  } {
    let notificationService = useToast ? toastNotification : flashNotification;
    let notificationType = NotificationType.ERROR;
    let triggerPageRefreshAndLogout = false;
    let triggerPageRefresh = false;
    let revalidateTag: RevalidateTags | undefined = undefined;
    const error = transformExceptionToErrorObject(err);
    const { formikErrors, generalErrors } = transformApiErrorsForFormik((error as GlueErrorResponse).errors);

    if (formikErrors && setErrors) {
      setErrors(formikErrors);
    }

    generalErrors?.forEach((error) => {
      const message = getErrorMessageFromAnyErrorLike(error);
      const code = getErrorCodeFromAnyErrorLike(error);

      if (isCartNeedsReviewErrorMessage(message) || isCartContainsNonBuyableItemsErrorMessage(message)) {
        triggerPageRefresh = true;
        notificationService = flashNotification;
      }

      if (isCartItemsPricesUpdatedTryAgainErrorCode(code)) {
        triggerPageRefresh = true;
        notificationService = flashNotification;
        notificationType = NotificationType.INFO;
        revalidateTag = RevalidateTags.CARTS;
        error.detail = dict.errorCodes.pricesHaveBeenUpdatedError;
      }

      if (isCartItemsPricesCouldNotBeValidatedTryAgainErrorCode(code)) {
        triggerPageRefresh = true;
        notificationService = flashNotification;
        revalidateTag = RevalidateTags.CARTS;
        error.detail = dict.cart.pricesCouldNotBeValidateError;
      }

      if (isStripePaymentErrorCode(code)) {
        triggerPageRefresh = true;
        notificationService = toastNotification;
        error.detail = dict.errorCodes.stripePaymentError;
      }

      if (isAuthenticationErrorMessage(message)) {
        triggerPageRefreshAndLogout = true;
        error.detail = dict.messages.generic.notAuthorized;
        notificationService = toastNotification;
      }

      notificationService(error.detail, notificationType);
    });

    return {
      triggerPageRefreshAndLogout,
      triggerPageRefresh,
      revalidateTag,
    };
  }

  return {
    countryOptions,
    salutationOptions,
    requiredMessage,
    handleErrors,
    setSubmitLoading,
    submitLoading,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isRequiredField(validationSchema: Yup.ObjectSchema<any>, fieldName: string) {
  const schemaDescription = validationSchema.describe();
  // eslint-disable-next-line security/detect-object-injection
  const field = schemaDescription.fields[fieldName];

  return !!(field && 'optional' in field && !field.optional);
}
